import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, Zap, TrendingUp, Shield, Target, ChevronDown, ChevronUp } from 'lucide-react';
import { LossStreakOptimizer, LossStreakOptimizationResult, LossStreakTestResult } from '@/services/loss-streak-optimizer';
import { ParsedCsvRow, AnalysisParams } from './types';
import { formatCurrency } from '@/utils/formatters';

interface AutoLossStreakOptimizerProps {
  csvData: ParsedCsvRow[];
  params: AnalysisParams;
  onApplySettings: (maxLosses: number) => void;
}

export const AutoLossStreakOptimizer: React.FC<AutoLossStreakOptimizerProps> = ({
  csvData,
  params,
  onApplySettings,
}) => {
  const [maxLossesToTest, setMaxLossesToTest] = useState(20);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [results, setResults] = useState<LossStreakTestResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const handleOptimize = async () => {
    if (csvData.length === 0) {
      return;
    }

    setIsOptimizing(true);
    try {
      // Run optimization in a setTimeout to avoid blocking UI
      setTimeout(() => {
        const optimizationResults = LossStreakOptimizer.findTopLossStreakSettings(
          csvData,
          maxLossesToTest,
          params
        );
        setResults(optimizationResults);
        setIsOpen(true);
        setIsOptimizing(false);
      }, 100);
    } catch (error) {
      console.error('Loss streak optimization failed:', error);
      setIsOptimizing(false);
    }
  };

  const handleApplySettings = (maxLosses: number) => {
    onApplySettings(maxLosses);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'optimal':
        return <Badge className="bg-green-500 hover:bg-green-600">✅ Optimal</Badge>;
      case 'good':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">⚠️ Good</Badge>;
      default:
        return <Badge variant="destructive">❌ Poor</Badge>;
    }
  };

  const getRatioBadge = (ratio: number) => {
    if (ratio >= 3.0) {
      return <span className="text-green-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    } else if (ratio >= 2.0) {
      return <span className="text-yellow-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    } else {
      return <span className="text-red-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    }
  };

  const getWinRateBadge = (winRate: number) => {
    if (winRate <= 50) {
      return <span className="text-red-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 70) {
      return <span className="text-orange-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 75) {
      return <span className="text-yellow-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else {
      return <span className="text-green-600 font-semibold">{winRate.toFixed(1)}%</span>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Zap size={18} />
          Auto Loss Streak Optimizer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Maximum losses to test</Label>
          <Input
            type="number"
            min="3"
            max="50"
            value={maxLossesToTest}
            onChange={(e) => setMaxLossesToTest(parseInt(e.target.value) || 20)}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            The optimizer will test 1 to {maxLossesToTest} consecutive losses to find the optimal setting
          </p>
        </div>

        <Button 
          onClick={handleOptimize}
          disabled={isOptimizing || csvData.length === 0}
          className="w-full"
        >
          {isOptimizing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Optimizing...
            </>
          ) : (
            <>
              <TrendingUp className="mr-2 h-4 w-4" />
              Find Optimal Settings
            </>
          )}
        </Button>

        {results.length > 0 && (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors p-2 rounded border">
                <span>
                  Found {results.length} settings ({results.filter(r => r.status === 'optimal').length} optimal)
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <div className="mt-4 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Max Losses</TableHead>
                      <TableHead className="text-right">Total Profit</TableHead>
                      <TableHead className="text-right">Max DD</TableHead>
                      <TableHead className="text-right">Profit/DD</TableHead>
                      <TableHead className="text-right">Win Rate</TableHead>
                      <TableHead className="text-right">Effectiveness</TableHead>
                      <TableHead className="text-right">Trades</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[80px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-center font-semibold">
                          {result.maxLosses}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <span className={result.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(result.totalProfit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(result.maxDrawdown)}
                        </TableCell>
                        <TableCell className="text-right">
                          {getRatioBadge(result.profitDDRatio)}
                        </TableCell>
                        <TableCell className="text-right">
                          {getWinRateBadge(result.winRate)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-orange-600">
                            {result.effectiveness.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {result.totalTrades}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(result.status)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApplySettings(result.maxLosses)}
                          >
                            Apply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-3 rounded border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <Target size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-blue-800 dark:text-blue-300">Anti Look-ahead Bias:</strong>
              <br />
              • Always uses "Resume AFTER win" (skips first winning trade)
              • Tests settings on chronological data only
              • Uses forward-walking validation to ensure realistic results
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};